import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import {
  isRequiredNumber,
  makeFloatRangeValidator
} from 'utilities/validators.js';
import { round6 as round } from 'utilities/math';

import Input from 'components/Input';
import Dropdown from '../Dropdown';
import RadioButton from '../RadioButton';

import {
  GREATER_THAN,
  LESS_THAN,
  NOT_EQUAL,
  GREATER_THAN_VALUE,
  LESS_THAN_VALUE,
  NOT_EQUAL_VALUE,
  HYPOTHESIS_MEAN,
  CONFIDENCE_INTERVAL,
  ONE_SAMPLE_PROPORTION,
  ONE_SAMPLE_T,
  TWO_SAMPLE_T,
  TWO_SAMPLE_PROPORTION,
  PAIRED_T
} from '../../Constants';

const optionsAlternativeHypothesis = [
  { value: GREATER_THAN_VALUE, label: GREATER_THAN, text: GREATER_THAN },
  { value: LESS_THAN_VALUE, label: LESS_THAN, text: LESS_THAN },
  { value: NOT_EQUAL_VALUE, label: NOT_EQUAL, text: NOT_EQUAL }
];

const ProcedureTypeRadioButtonsComponent = ({
  procedureOneSample,
  inferentialStatistics
}) => {
  const isOneSampleProportion = inferentialStatistics === ONE_SAMPLE_PROPORTION;
  const isOneSampleT = inferentialStatistics === ONE_SAMPLE_T;
  const isTwoSampleT = inferentialStatistics === TWO_SAMPLE_T;
  const isTwoSampleProportion = inferentialStatistics === TWO_SAMPLE_PROPORTION;
  const isPairedT = inferentialStatistics === PAIRED_T;

  let hypothesisTitle = '';
  if (isOneSampleProportion) {
    hypothesisTitle = 'Hypothesized Proportion';
  } else if (isOneSampleT) {
    hypothesisTitle = 'Hypothesized Mean';
  } else if (isTwoSampleT || isTwoSampleProportion || isPairedT) {
    hypothesisTitle = 'Hypothesized Difference';
  }

  let confidenceTitle = '';
  if (isOneSampleT) {
    confidenceTitle = 'Confidence Interval for μ';
  } else if (isOneSampleProportion || isTwoSampleT || isTwoSampleProportion || isPairedT) {
    confidenceTitle = 'Confidence Interval';
  }

  const hypothesizedProportionValidator = makeFloatRangeValidator(0, 1);

  const formatFn = value => (value || value === 0 ? round(value) : '');

  return (
    <div className="flex-column">
      <RadioButton
        name="procedureOneSample"
        value={HYPOTHESIS_MEAN}
        labelClassName="mb-10"
      >
        Hypothesis Test
      </RadioButton>
      {procedureOneSample === HYPOTHESIS_MEAN && (
        <div className="flex space-between baseline">
          {isOneSampleProportion && (
            <Field
              type="number"
              id="hypothesisProportion"
              component={Input}
              name="hypothesisProportion"
              step={0.1}
              label={hypothesisTitle}
              pattern="\d+"
              format={formatFn}
              validate={hypothesizedProportionValidator}
              wrapperClassName="relative width-48 iconTop two-lines mr-5"
            />
          )}

          {(isOneSampleT || isTwoSampleT) && (
            <Field
              type="number"
              id="hypothesisMean"
              component={Input}
              name="hypothesisMean"
              step={1}
              label={hypothesisTitle}
              pattern="\d+"
              format={formatFn}
              validate={isRequiredNumber}
              labelClassName="main-input__label width-50"
              wrapperClassName="relative width-48 iconTop two-lines mr-5"
            />
          )}
          {isTwoSampleProportion && (
            <Field
              type="number"
              id="dfHypothesisProportion"
              component={Input}
              name="dfHypothesisProportion"
              step={1}
              label={hypothesisTitle}
              pattern="\d+"
              format={formatFn}
              validate={isRequiredNumber}
              labelClassName="main-input__label width-50"
              wrapperClassName="relative width-48 iconTop two-lines mr-5"
            />
          )}
          {isPairedT && (
            <Field
              type="number"
              id="pairedT.hypothesisMean"
              component={Input}
              name="pairedT.hypothesisMean"
              step={1}
              label={hypothesisTitle}
              pattern="\d+"
              format={formatFn}
              validate={isRequiredNumber}
              labelClassName="main-input__label width-50"
              wrapperClassName="relative width-48 iconTop two-lines mr-5"
            />
          )}

          <Dropdown
            optionsList={optionsAlternativeHypothesis}
            label="Alternative Hypothesis"
            name="alternativeHypothesis"
            id="alternativeHypothesisDropdown"
            placeholder="Select an alternative hypothesis"
            wrapperClassName="relative width-48 iconTop two-lines"
          />
        </div>
      )}
      <RadioButton
        value={CONFIDENCE_INTERVAL}
        name="procedureOneSample"
      >
        {confidenceTitle}
      </RadioButton>
      {procedureOneSample === CONFIDENCE_INTERVAL && (
        <div className="flex baseline main-input--inline ml-10 mr-10">
          <Field
            type="number"
            id="confidenceInterval"
            component={Input}
            name="confidenceInterval"
            label="Confidence Interval"

            inputClassName="main-input__input ml-3 mt-20 max-85"
            labelClassName="main-input__label"
            wrapperClassName="relative flex baseline space-between iconTop width-full"

            pattern="\d+"
            format={formatFn}
            validate={makeFloatRangeValidator(0.0001, 99.9999)}
            min={1}
            max={99}
          />
          <span className="main-input__label ml-10">%</span>
        </div>
      )}
    </div>
  );
};

ProcedureTypeRadioButtonsComponent.propTypes = {
  procedureOneSample: PropTypes.string,
  inferentialStatistics: PropTypes.string
};

export default ProcedureTypeRadioButtonsComponent;
